import { useAlert } from '@dwelvio/react-alert';
import { useMutation } from '@apollo/client/react';

import {
  UnitFragment,
  DeleteUnitDocument,
  ArchiveUnitDocument,
  DeleteUnitMutation,
  DeleteUnitMutationVariables,
  ArchiveUnitMutation,
  ArchiveUnitMutationVariables,
  StartRentingUnitMutation,
  StartRentingUnitDocument,
  StartRentingUnitMutationVariables,
  StopRentingUnitMutation,
  StopRentingUnitMutationVariables,
  StopRentingUnitDocument,
} from '../../../generated/graphql';
import { Button, Stack } from '@mui/material';
import React from 'react';

import { ConfirmationDialog } from '../../../components/confirmation-dialog';

interface UnitDetailActionMenuProps {
  unit: UnitFragment;
  loading?: boolean;
  disabled?: boolean;
}

export function UnitDetailActionMenu(props: UnitDetailActionMenuProps) {
  const { unit: p, loading, disabled } = props;
  const { show } = useAlert();
  const [unitToDelete, setUnitToDelete] = React.useState<UnitFragment | null>(null);
  const [unitToUndelete, setUnitToUndelete] = React.useState<UnitFragment | null>(null);
  const [unitToStartRenting, setUnitToStartRenting] = React.useState<UnitFragment | null>(null);
  const [unitToStopRenting, setUnitToStopRenting] = React.useState<UnitFragment | null>(null);
  const [unitToArchive, setUnitToArchive] = React.useState<UnitFragment | null>(null);
  const [unitToUnarchive, setUnitToUnarchive] = React.useState<UnitFragment | null>(null);

  const [deleteUnit, { loading: deleteLoading }] = useMutation<DeleteUnitMutation, DeleteUnitMutationVariables>(
    DeleteUnitDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa zmazať nehnuteľnosť.', e.message),
    },
  );

  const [startRentingUnit, { loading: startRentingLoading }] = useMutation<
    StartRentingUnitMutation,
    StartRentingUnitMutationVariables
  >(StartRentingUnitDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa začať prenajímať nehnuteľnosť.', e.message),
    onCompleted: () => {
      show.info('Nehnuteľnosť je možné začať prenajímať.');
    },
  });

  const [stopRentingUnit, { loading: stopRentingLoading }] = useMutation<
    StopRentingUnitMutation,
    StopRentingUnitMutationVariables
  >(StopRentingUnitDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa ukončiť prenajímateľnosť.', e.message),
    onCompleted: () => {
      show.info('Nehnuteľnosť prestalo byť možné prenajímať.');
    },
  });

  const [archiveUnit, { loading: archiveLoading }] = useMutation<ArchiveUnitMutation, ArchiveUnitMutationVariables>(
    ArchiveUnitDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa archivovať nehnuteľnosť.', e.message),
      onCompleted: () => {
        show.info('Nehnuteľnosť bola archivovaná.');
      },
    },
  );

  return (
    <Stack spacing={1}>
      {p.status === 'not_renting' && (
        <Button
          onClick={() => setUnitToStartRenting(p)}
          disabled={startRentingLoading || disabled}
          variant="outlined"
          fullWidth
        >
          Začať prenajímať
        </Button>
      )}
      {p.status === 'renting' && (
        <Button
          onClick={() => setUnitToStopRenting(p)}
          disabled={stopRentingLoading || disabled}
          variant="outlined"
          fullWidth
        >
          Neprenajímať
        </Button>
      )}
      {p.status !== 'archived' && (
        <Button onClick={() => setUnitToArchive(p)} disabled={archiveLoading || disabled} variant="outlined" fullWidth>
          Archivovať
        </Button>
      )}
      {p.status === 'archived' && (
        <Button
          onClick={() => setUnitToUnarchive(p)}
          disabled={archiveLoading || disabled}
          variant="outlined"
          fullWidth
        >
          Odarchivovať
        </Button>
      )}
      {!p.deletedAt && (
        <Button
          onClick={() => setUnitToDelete(p)}
          disabled={deleteLoading || disabled}
          color="error"
          variant="outlined"
          fullWidth
        >
          Zmazať
        </Button>
      )}

      {unitToDelete && (
        <ConfirmationDialog
          title={'Odstránenie nehnuteľnosti'}
          loading={deleteLoading}
          cancelLabel="Nie"
          onCancel={() => setUnitToDelete(null)}
          confirmLabel="Áno"
          onConfirm={() => {
            deleteUnit({ variables: { unitId: unitToDelete.id } });
            setUnitToDelete(null);
          }}
          message={`Ste si istí, že chcete odstrániť nehnuteľnosť '${unitToDelete.name}'?`}
        />
      )}

      {unitToStartRenting && (
        <ConfirmationDialog
          title="Začať prenajímať nehnuteľnosť"
          loading={startRentingLoading}
          cancelLabel="Nie"
          onCancel={() => setUnitToStartRenting(null)}
          confirmLabel="Áno"
          onConfirm={() => {
            startRentingUnit({ variables: { unitId: unitToStartRenting.id } });
            setUnitToStartRenting(null);
          }}
          message={`Ste si istí, že chcete začať prenajímať nehnuteľnosť '${unitToStartRenting.name}'?`}
        />
      )}

      {unitToStopRenting && (
        <ConfirmationDialog
          title="Ukončiť prenajímanie nehnuteľnosti"
          loading={stopRentingLoading}
          cancelLabel="Nie"
          onCancel={() => setUnitToStopRenting(null)}
          confirmLabel="Áno"
          onConfirm={() => {
            stopRentingUnit({ variables: { unitId: unitToStopRenting.id } });
            setUnitToStopRenting(null);
          }}
          message={`Ste si istí, že už nechcete nehnuteľnosť '${unitToStopRenting.name}' prenajímať?\n Aktívne prenájmy zostanú nezmenené, len ju nebude možné použiť v nových prenájmoch.`}
        />
      )}

      {unitToArchive && (
        <ConfirmationDialog
          title="Archivácia nehnuteľnosti"
          loading={archiveLoading}
          cancelLabel="Nie"
          onCancel={() => setUnitToArchive(null)}
          confirmLabel="Áno"
          onConfirm={() => {
            archiveUnit({ variables: { unitId: unitToArchive.id } });
            setUnitToArchive(null);
          }}
          message={`Ste si istí, že chcete archivovať nehnuteľnosť '${unitToArchive.name}'? Nehnuteľnosť musí mať ukončené všetky zmluvy a dokončené všetky úlohy.`}
        />
      )}
    </Stack>
  );
}
